import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { addSupplierBrand, renamePurchaseBrand, type BrandOverviewDto, type SupplierOverviewDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { isOwnBrand, isSpellingCorrection } from '@/lib/supplierDirectory'
import { cn } from '@/lib/utils'

interface BrandListTableProps {
  brands: BrandOverviewDto[]
  suppliers: SupplierOverviewDto[]
  canManage: boolean
  manageHint?: string
  /** Reloads the page's directory; the rename awaits it before giving the name back. */
  onChanged: () => void | Promise<void>
}

/** « Par marque » — one row per brand with who supplies it; sold brands nobody supplies stand out (§ P3). */
export function BrandListTable({ brands, suppliers, canManage, manageHint, onChanged }: BrandListTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Marque</TableHead>
            <TableHead>Fournisseurs</TableHead>
            <TableHead className="w-0" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {brands.map((b) => (
            <TableRow
              key={b.id ?? `sold-${b.name}`}
              className={cn(b.suppliers.length === 0 && 'bg-amber-50/60')}
              data-testid="brands-row"
            >
              <TableCell className="font-medium">
                <BrandName brand={b} canManage={canManage} onChanged={onChanged} />
              </TableCell>
              <TableCell>
                {b.suppliers.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {b.suppliers.map((s) => (
                      <Badge
                        key={s.id}
                        variant="outline"
                        className={cn('font-normal', isOwnBrand(s.name, b.name) && 'border-primary text-primary')}
                      >
                        {s.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant="outline" className="border-amber-300 bg-amber-100 font-normal text-amber-900">
                      Aucun fournisseur
                    </Badge>
                    {b.soldLineCount > 0 && (
                      <span className="text-muted-foreground">
                        vendue sur {b.soldLineCount} ligne{b.soldLineCount > 1 ? 's' : ''} ces 12 derniers mois
                      </span>
                    )}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {b.suppliers.length === 0 && (
                  <ChooseSupplier brandName={b.name} suppliers={suppliers} disabled={!canManage} hint={manageHint} onChanged={onChanged} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function ChooseSupplier({
  brandName,
  suppliers,
  disabled,
  hint,
  onChanged,
}: {
  brandName: string
  suppliers: SupplierOverviewDto[]
  disabled: boolean
  hint?: string
  onChanged: () => void | Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [supplierId, setSupplierId] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!supplierId) return
    setSaving(true)
    try {
      await addSupplierBrand(supplierId, brandName)
      setOpen(false)
      onChanged()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "La marque n'a pas pu être ajoutée.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} title={hint} data-testid="brands-row-choose-supplier-btn">
          Choisir un fournisseur
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3">
        <p className="text-sm">
          Chez qui commandez-vous <span className="font-medium">{brandName}</span> ?
        </p>
        <Select value={supplierId} onValueChange={setSupplierId}>
          <SelectTrigger data-testid="brands-choose-supplier-select">
            <SelectValue placeholder="Fournisseur" />
          </SelectTrigger>
          <SelectContent>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!supplierId || saving} data-testid="brands-choose-supplier-save-btn">
            Ajouter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** A brand name corrects in place (case and spacing only, V1). A sold-only brand has nothing to rename yet. */
function BrandName({ brand, canManage, onChanged }: { brand: BrandOverviewDto; canManage: boolean; onChanged: () => void | Promise<void> }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(brand.name)
  const [error, setError] = useState<string | null>(null)
  // One rename at a time: Enter then blur must not send twice, and nothing typed meanwhile is dropped.
  const [saving, setSaving] = useState(false)

  if (!brand.id || !canManage || !editing) {
    return (
      <button
        type="button"
        className={cn('text-left', brand.id && canManage && 'hover:underline')}
        onClick={() => {
          if (!brand.id || !canManage) return
          // The draft starts from the current spelling, not from a previous edit.
          setValue(brand.name)
          setEditing(true)
        }}
        disabled={!brand.id || !canManage}
        data-testid="brands-row-name-btn"
      >
        {brand.name}
      </button>
    )
  }

  const cancel = () => {
    setValue(brand.name)
    setError(null)
    setEditing(false)
  }

  const save = async (explain: boolean) => {
    if (saving) return
    if (value.trim() === brand.name) {
      setEditing(false)
      return
    }
    if (!isSpellingCorrection(brand.name, value)) {
      // Leaving the field abandons an impossible rename; pressing Enter says why it is impossible.
      if (!explain) {
        cancel()
        return
      }
      setError(`Seules la casse et les espaces se corrigent. Créez la marque ${value.trim()} et ajoutez-la aux fournisseurs concernés.`)
      return
    }
    setSaving(true)
    try {
      await renamePurchaseBrand(brand.id!, value)
      // The editor stays locked until the new spelling is reloaded: reopened earlier, it would start
      // from the old one and a blur would undo the rename.
      await onChanged()
      setEditing(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "La marque n'a pas pu être renommée.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-1">
      <Input
        autoFocus
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save(true)
          // A rename in flight cannot be cancelled: Escape would only hide it and leave a stale draft.
          if (e.key === 'Escape' && !saving) cancel()
        }}
        onBlur={() => void save(false)}
        readOnly={saving}
        className="h-8 w-48"
        aria-label="Nom de la marque"
        data-testid="brands-row-name-input"
      />
      {error && (
        <p className="max-w-xs text-xs text-destructive" data-testid="brands-row-name-error">
          {error}
        </p>
      )}
    </div>
  )
}
